import type {
  AssistantSource,
  WatchNote,
  WatchNoteChainLink,
  WatchNoteCite,
  WatchNoteDocument,
  WatchNoteFact,
  WatchNoteFactKind,
  WatchNoteStatusChange,
} from "@hp/api-client";

/**
 * Pure helpers behind the sourced watch-note card: what a source chip says,
 * how the facts are grouped, and what the document viewer is asked to open.
 * Kept free of React (type imports only) so they can be checked on their own.
 */

/** "2017-07-11" -> "11.07.2017": the way Indian orders write dates, so a chip reads like the order it points at. */
export function dottedDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** "90" -> "¶90"; a paragraph the server already wrote with a mark or a word ("¶90", "para 9") is kept as given. */
function paraText(para: string | null): string | null {
  const p = para?.trim();
  if (!p) return null;
  return /^\d/.test(p) ? `¶${p}` : p;
}

/**
 * The short text on a source chip, e.g. "¶90 · p43 · 11.07.2017". The case
 * reference is left out because the chip sits under a fact of a note that
 * already names its case; the full `label` goes in the hint instead. Falls
 * back to the label (or file name) when the cite has no paragraph, page or date.
 */
export function citeChipText(cite: WatchNoteCite): string {
  const parts = [paraText(cite.para), cite.page ? `p${cite.page}` : null, dottedDate(cite.date)].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return cite.label || cite.file_name;
}

/** Whether a cite can be opened: only a held document can. */
export function citeIsHeld(cite: Pick<WatchNoteCite, "document_id">): boolean {
  return Boolean(cite.document_id);
}

/** What the card asks SourceDocumentViewer to show. */
export interface ViewerRequest {
  source: AssistantSource;
  /** Set only for a quote that was found on the cited page: the viewer then highlights it. */
  passage?: { text: string; page: number };
  /** Without a passage: the page the document opens at. */
  startPage?: number | null;
}

/** Said when a cite has words but they were not found on its page, so the reader knows to look rather than trust a highlight. */
export const QUOTE_NOT_VERIFIED = "quote not verified on this page";

/**
 * Where a cite opens: at its page with the quote highlighted when the quote
 * was verified on that page; otherwise at the page alone, and the title then
 * says the quote was not verified. Null for a document not held.
 */
export function viewerRequestForCite(cite: WatchNoteCite): ViewerRequest | null {
  if (!cite.document_id) return null;
  const base = cite.label || cite.file_name;
  const quote = cite.quote?.trim();
  if (quote && cite.quote_verified && cite.page) {
    return { source: { type: "source_document", id: cite.document_id, label: base }, passage: { text: quote, page: cite.page } };
  }
  return {
    source: { type: "source_document", id: cite.document_id, label: quote ? `${base} · ${QUOTE_NOT_VERIFIED}` : base },
    startPage: cite.page ?? 1,
  };
}

/** A chain order as a cite, so a HELD badge opens exactly like a source chip. Null when the order is not held or has no document id. */
export function chainLinkCite(link: WatchNoteChainLink): WatchNoteCite | null {
  if (!link.held || !link.document_id) return null;
  const ref = link.forum && !link.ref.toUpperCase().startsWith(link.forum.toUpperCase()) ? `${link.forum} ${link.ref}` : link.ref;
  return {
    document_id: link.document_id,
    file_name: ref,
    page: link.page ?? null,
    para: null,
    date: link.date,
    quote: link.quote ?? null,
    quote_verified: Boolean(link.quote_verified),
    label: [ref, link.page ? `p${link.page}` : null, dottedDate(link.date) ? `(${dottedDate(link.date)})` : null].filter(Boolean).join(" "),
  };
}

/**
 * The note's own document. An older server sends only `source_document_id`
 * and `file_name`, so the header link still works without `document`.
 */
export function noteDocument(note: Pick<WatchNote, "document" | "source_document_id" | "file_name">): WatchNoteDocument | null {
  if (note.document) return note.document;
  if (!note.source_document_id) return null;
  return { id: note.source_document_id, file_name: note.file_name, date: null, forum: null, case_ref: null, citation: null };
}

/** The note's own document opened at page 1 (its first page is where a reader starts). */
export function viewerRequestForDocument(doc: WatchNoteDocument): ViewerRequest {
  const label = [doc.forum, doc.case_ref].filter(Boolean).join(" ") || doc.file_name;
  return { source: { type: "source_document", id: doc.id, label }, startPage: 1 };
}

export type FactGroupKey = "holds" | "our_side" | "value" | "action" | "other";

export const FACT_GROUP_HEADING: Record<FactGroupKey, string> = {
  holds: "What it holds",
  our_side: "Our side",
  value: "Value",
  action: "Action",
  other: "Also noted",
};

// A finding of fact and a rule applied are both part of what the ruling holds;
// a lawyer reads them together, so they share the first heading.
const KIND_TO_GROUP: Record<WatchNoteFactKind, FactGroupKey> = {
  holding: "holds",
  finding: "holds",
  rule: "holds",
  our_side: "our_side",
  value: "value",
  action: "action",
  other: "other",
};

const GROUP_ORDER: FactGroupKey[] = ["holds", "our_side", "value", "action", "other"];

/**
 * Facts grouped under the card's headings, in reading order (what it holds,
 * our side, value, action), keeping the server's order inside a group. An
 * unknown kind from a newer server lands in "Also noted" rather than being
 * dropped. Empty groups are left out.
 */
export function groupFacts(facts: WatchNoteFact[]): { key: FactGroupKey; heading: string; facts: WatchNoteFact[] }[] {
  const byGroup = new Map<FactGroupKey, WatchNoteFact[]>();
  for (const fact of facts) {
    const key = KIND_TO_GROUP[fact.kind] ?? "other";
    byGroup.set(key, [...(byGroup.get(key) ?? []), fact]);
  }
  return GROUP_ORDER.filter((k) => byGroup.has(k)).map((key) => ({ key, heading: FACT_GROUP_HEADING[key], facts: byGroup.get(key)! }));
}

/** Facts win over lines when the server sent any; an older server (or an empty list) falls back to the plain lines. */
export function hasFacts(note: Pick<WatchNote, "facts">): boolean {
  return Array.isArray(note.facts) && note.facts.length > 0;
}

/**
 * Status changes oldest first. The server already sends them that way; sorting
 * again keeps the timeline right if it ever does not. Undated entries keep
 * their place relative to each other and go last, since they cannot be placed in time.
 */
export function statusHistoryOldestFirst(history: WatchNoteStatusChange[] | undefined): WatchNoteStatusChange[] {
  if (!history?.length) return [];
  const dated = history.map((h, i) => ({ h, i })).filter((x) => x.h.date);
  dated.sort((a, b) => (a.h.date as string).localeCompare(b.h.date as string) || a.i - b.i);
  return [...dated.map((d) => d.h), ...history.filter((h) => !h.date)];
}

export type StatusTone = "neutral" | "warning" | "danger" | "success";

/**
 * How alarming a standing is for reliance: set aside is a precedent gone,
 * stayed or "not given effect" means it cannot be relied on yet, final means
 * it can. Matched on words because the statuses are free text from the fold.
 */
export function statusTone(status: string): StatusTone {
  const s = status.toLowerCase();
  if (/set aside|quashed|overruled|reversed/.test(s)) return "danger";
  // A challenge that failed leaves the precedent standing: checked before "stay" so
  // "stay application dismissed" does not read as a stay.
  if (/(stay|appeal|application|petition)\b.*\b(dismissed|rejected|withdrawn|refused)/.test(s)) return "success";
  if (/stay|not given effect|pending|compute only|admitted|remand/.test(s)) return "warning";
  if (/final|upheld|affirmed|dismissed/.test(s)) return "success";
  return "neutral";
}
